/**
 * Top app bar. Shows the mobile menu toggle, the current user's email, and a
 * logout button. Logout clears the session and returns to /login.
 */
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/Forms/Button';
import Logo from '@/components/common/Logo';

export interface HeaderProps {
  /** Opens the mobile sidebar drawer. */
  onMenuClick?: () => void;
}

/** Sticky top header with user info + logout. */
export default function Header({ onMenuClick }: HeaderProps): ReactElement {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 lg:hidden"
          aria-label="Mở menu"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="flex items-center gap-2 lg:hidden">
          <Logo className="h-7 w-7" />
          <h1 className="text-base font-bold text-gray-900">Ads360</h1>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {user && (
          <span className="hidden max-w-[200px] truncate text-sm text-gray-600 sm:inline" title={user.email}>
            {user.email}
          </span>
        )}
        <Button variant="secondary" onClick={handleLogout}>
          Đăng xuất
        </Button>
      </div>
    </header>
  );
}
